/**
 * Good Sudoku, application controller.
 *
 * Owns board rendering, the dual input model (number-first & cell-first),
 * drag-to-highlight multi-select, peer / same-digit / conflict highlighting,
 * the pencil / accent / cross-out note system, the hint teacher, undo/redo, a
 * pausable timer, themes, the Learn section and persistence. All game logic is
 * delegated to the shared engine in /core.
 */

import { colOf, rowOf } from "../core/board.js";
import {
  DIFFICULTIES,
  DIFFICULTY_BY_ID,
  DifficultyId,
} from "../core/difficulty.js";
import { Hint } from "../core/hint.js";
import { NoteKind, SudokuGame } from "../core/game.js";
import { PuzzleSource } from "./puzzleSource.js";
import { LearnView } from "./learn.js";
import { el, fmtTime, on } from "./dom.js";
import { icons } from "./icons.js";
import { hasDigit } from "../core/board.js";
import { sound } from "./sound.js";
import { DevColorEditor, applyThemeOverrides, effectiveThemes } from "./devcolors.js";
import { load, save } from "./storage.js";

type Mode = "normal" | "notes";

interface ThemeDef {
  id: string;
  name: string;
  bg: string;
  fg: string;
  accent: string;
}

/** The ten selectable visual themes. `classic` (yellow/white/gray/black) is the default. */
const THEMES: ThemeDef[] = [
  { id: "classic", name: "Classic", bg: "#eee9e7", fg: "#161718", accent: "#f5b400" },
  { id: "light", name: "Daylight", bg: "#f3f3f0", fg: "#2a2f36", accent: "#2566f0" },
  { id: "dark", name: "Dark", bg: "#1b232c", fg: "#e8eef4", accent: "#3f7bff" },
  { id: "midnight", name: "Midnight", bg: "#121838", fg: "#dbe4ff", accent: "#22d3ee" },
  { id: "mint", name: "Mint", bg: "#eee9e7", fg: "#14342a", accent: "#10b981" },
  { id: "rose", name: "Rose", bg: "#eee9e7", fg: "#43182f", accent: "#ec4899" },
  { id: "slate", name: "Slate", bg: "#eee9e7", fg: "#23303d", accent: "#5b7089" },
  { id: "forest", name: "Forest", bg: "#12281d", fg: "#d8f3e6", accent: "#34d399" },
  { id: "grape", name: "Grape", bg: "#231635", fg: "#ece0ff", accent: "#a855f7" },
  { id: "mono", name: "Mono", bg: "#eee9e7", fg: "#0d1117", accent: "#3a3a3a" },
];
interface Settings {
  theme: string;
  assist: boolean;
  autoCandidates: boolean;
  autoSolve: boolean;
  sound: boolean;
  highlightPeers: boolean;
  highlightSame: boolean;
  showTimer: boolean;
  showCounts: boolean;
}

const DEFAULT_SETTINGS: Settings = {
  theme: "classic",
  assist: true,
  autoCandidates: true,
  autoSolve: true,
  sound: true,
  highlightPeers: true,
  highlightSame: true,
  showTimer: true,
  showCounts: true,
};

// Difficulty accent colors resolve to the current theme's variables, so they
// stay in line with whichever theme is active.
const DIFF_COLOR: Record<DifficultyId, string> = {
  easy: "var(--easy)",
  medium: "var(--medium)",
  hard: "var(--hard)",
  expert: "var(--expert)",
  insane: "var(--insane)",
};

const STATIC_CLASS: string[] = (() => {
  const out: string[] = [];
  for (let i = 0; i < 81; i++) {
    const r = rowOf(i);
    const c = colOf(i);
    const cls = ["cell"];
    if (c % 3 === 0) cls.push("box-left");
    if (r % 3 === 0) cls.push("box-top");
    if (c % 3 === 2) cls.push("box-right");
    if (r % 3 === 2) cls.push("box-bottom");
    out.push(cls.join(" "));
  }
  return out;
})();

export class GoodSudokuApp {
  private source = new PuzzleSource();
  private game: SudokuGame | null = null;
  private difficulty: DifficultyId = "easy";

  private selected: number | null = null;
  private multi = new Set<number>();
  private armed: number | null = null;
  // The last given/entered digit the player highlighted. It stays lit until they
  // click off the board or highlight a different value, even across empty-cell taps.
  private lastDigit: number | null = null;
  private mode: Mode = "normal";
  private noteKind: NoteKind = "pencil";
  private hintCells = new Set<number>();

  private elapsed = 0;
  private timerId: number | null = null;
  private isDaily = false;
  private paused = false;

  // drag-to-highlight
  private pointerDown = false;
  private ctrlDown = false;
  private dragStart: number | null = null;
  private dragMoved = false;
  private dragSet = new Set<number>();

  private settings: Settings = { ...DEFAULT_SETTINGS, ...load<Partial<Settings>>("settings", {}) };

  // DOM refs
  private root: HTMLElement;
  private boardEl!: HTMLDivElement;
  private cellEls: HTMLDivElement[] = [];
  private valueEls: HTMLSpanElement[] = [];
  private noteSpanEls: HTMLSpanElement[][] = [];
  private numEls: HTMLButtonElement[] = [];
  private timeEl!: HTMLElement;
  private timeStat!: HTMLElement;
  private diffChipEl!: HTMLElement;
  private mistakeEl!: HTMLElement;
  private notesBtn!: HTMLButtonElement;
  private undoBtn!: HTMLButtonElement;
  private redoBtn!: HTMLButtonElement;
  private notekindRow!: HTMLElement;
  private hintPanel!: HTMLElement;
  private scrim!: HTMLElement;
  private settingsSheet!: HTMLElement;
  private diffSheet!: HTMLElement;
  private learnSheet!: HTMLElement;
  private kbdOverlay!: HTMLElement;
  private appShell!: HTMLElement;
  private actionEls!: Record<string, HTMLButtonElement>;
  private pauseOverlay!: HTMLElement;
  private toastEl!: HTMLElement;
  private learn!: LearnView;
  private devEditor?: DevColorEditor;
  private lastValues = new Int8Array(81);

  constructor(root: HTMLElement) {
    this.root = root;
    sound.enabled = this.settings.sound;
    applyThemeOverrides();
    this.applyTheme();
    this.buildShell();
    this.bindKeyboard();
    this.bindSystem();
    // Unlock audio on the first user gesture (browser autoplay policy).
    const unlock = () => sound.unlock();
    on(document, "pointerdown", unlock, { once: true });
    on(document, "keydown", unlock, { once: true });
    void this.start();
  }

  // -------------------------------------------------------------- theme

  private applyTheme(): void {
    const ids = new Set(effectiveThemes(THEMES).map((t) => t.id));
    const id = ids.has(this.settings.theme) ? this.settings.theme : "classic";
    document.documentElement.dataset.theme = id;
  }

  /** A theme's own resolved swatch colors (including any dev-tool overrides),
   *  read by momentarily applying it, never the currently active theme's. */
  private themeColors(id: string): {
    bg: string;
    fg: string;
    accent: string;
    surface: string;
  } {
    const root = document.documentElement;
    const prev = root.dataset.theme;
    root.dataset.theme = id;
    const cs = getComputedStyle(root);
    const read = (v: string, fallback: string) => cs.getPropertyValue(v).trim() || fallback;
    const colors = {
      bg: read("--bg", "#eee9e7"),
      fg: read("--ink", "#161718"),
      accent: read("--primary", "#888888"),
      surface: read("--surface-2", "#f0f0f0"),
    };
    if (prev === undefined) delete root.dataset.theme;
    else root.dataset.theme = prev;
    return colors;
  }

  private bindSystem(): void {
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.pause();
    });
    // A soft click for UI chrome buttons. The board, number pad and action
    // buttons make their own (more specific) sounds, so skip those.
    on(this.root, "click", (e) => {
      const t = (e.target as HTMLElement).closest("button");
      if (!t) return;
      if (
        t.classList.contains("num") ||
        t.classList.contains("action") ||
        t.classList.contains("notekind")
      )
        return;
      sound.play("button");
    });
    // Clicking off the board (but not on the number pad / controls, which are
    // gameplay) drops the persistent highlight and the selection.
    on(document, "pointerdown", (e) => {
      if (this.selected === null && this.lastDigit === null && this.multi.size === 0) return;
      const t = e.target as HTMLElement | null;
      if (!t || t.closest(".board") || t.closest(".controls")) return;
      this.selected = null;
      this.multi.clear();
      this.lastDigit = null;
      this.render();
    });
  }

  // -------------------------------------------------------------- bootstrap

  private async start(): Promise<void> {
    const saved = load<{ difficulty: DifficultyId; game: string; elapsed: number; isDaily: boolean } | null>(
      "current",
      null,
    );
    if (saved && saved.game) {
      try {
        this.game = SudokuGame.deserialize(saved.game);
        this.difficulty = saved.difficulty;
        this.elapsed = saved.elapsed ?? 0;
        this.isDaily = saved.isDaily ?? false;
        this.afterLoad();
        return;
      } catch {
        /* fall through */
      }
    }
    await this.newGame("easy");
  }

  async newGame(difficulty: DifficultyId, daily = false): Promise<void> {
    this.difficulty = difficulty;
    this.isDaily = daily;
    this.closeSheets();
    this.diffChipEl.innerHTML = `<span class="loading"><span class="spinner"></span>Generating…</span>`;
    const puzzle = daily
      ? await this.source.daily(new Date().toISOString().slice(0, 10), difficulty)
      : await this.source.get(difficulty);
    this.game = new SudokuGame(puzzle, {
      assist: this.settings.assist,
      autoCandidates: this.settings.autoCandidates,
      autoSolve: this.settings.autoSolve,
    });
    this.elapsed = 0;
    this.afterLoad();
  }

  private afterLoad(): void {
    this.selected = null;
    this.armed = null;
    this.lastDigit = null;
    this.multi.clear();
    this.mode = "normal";
    this.paused = false;
    this.boardEl.classList.remove("paused");
    this.pauseOverlay.classList.remove("show");
    this.hintCells.clear();
    this.startTimer();
    this.render();
    this.persist();
  }

  // -------------------------------------------------------------- DOM build

  private buildShell(): void {
    const shell = el("div", "app-shell");
    this.appShell = shell;

    // Top bar
    const topbar = el("div", "topbar");
    this.diffChipEl = el("div", "diff-chip");
    on(this.diffChipEl, "click", () => this.openDiffSheet());
    const right = el("div", "topbar");
    right.style.gap = "10px";

    const mistakeWrap = el("div", "stat");
    mistakeWrap.innerHTML = `<span class="label">Mistakes</span>`;
    this.mistakeEl = el("span", "value", "0");
    mistakeWrap.appendChild(this.mistakeEl);

    this.timeStat = el("div", "stat clickable");
    this.timeStat.innerHTML = `<span class="label">Time</span>`;
    this.timeEl = el("span", "value", "0:00");
    this.timeStat.appendChild(this.timeEl);
    this.timeStat.title = "Tap to pause";
    on(this.timeStat, "click", () => this.togglePause());

    const kbdBtn = el("button", "icon-btn");
    kbdBtn.innerHTML = icons.keyboard;
    kbdBtn.title = "Keyboard shortcuts (K)";
    on(kbdBtn, "click", () => this.toggleKbdOverlay());

    const learnBtn = el("button", "icon-btn");
    learnBtn.innerHTML = icons.learn;
    learnBtn.title = "Learn techniques";
    on(learnBtn, "click", () => this.openLearn());

    const newBtn = el("button", "icon-btn");
    newBtn.innerHTML = icons.grid;
    newBtn.title = "New game";
    on(newBtn, "click", () => this.openDiffSheet());

    const setBtn = el("button", "icon-btn");
    setBtn.innerHTML = icons.settings;
    setBtn.title = "Settings";
    on(setBtn, "click", () => this.openSettings());

    right.append(mistakeWrap, this.timeStat, kbdBtn, learnBtn, newBtn, setBtn);
    topbar.append(this.diffChipEl, right);

    // Board
    const boardWrap = el("div", "board-wrap");
    const board = el("div", "board");
    this.boardEl = board;
    for (let i = 0; i < 81; i++) {
      const cell = el("div", STATIC_CLASS[i]);
      cell.dataset.index = String(i);
      const value = el("span", "value");
      value.style.display = "none";
      const notes = el("div", "notes");
      const spans: HTMLSpanElement[] = [el("span")];
      for (let d = 1; d <= 9; d++) {
        const s = el("span");
        notes.appendChild(s);
        spans.push(s);
      }
      cell.append(value, notes);
      board.appendChild(cell);
      this.cellEls.push(cell);
      this.valueEls.push(value);
      this.noteSpanEls.push(spans);
    }
    this.bindBoardPointer(board);

    this.pauseOverlay = el("div", "pause-overlay");
    const resumeBtn = el("button");
    resumeBtn.innerHTML = `${icons.play}<span>Resume</span>`;
    on(resumeBtn, "click", () => this.resume());
    this.pauseOverlay.appendChild(resumeBtn);
    boardWrap.append(board, this.pauseOverlay);

    // Controls
    const controls = el("div", "controls");
    const actions = el("div", "actions");
    this.undoBtn = this.makeAction(icons.undo, "Undo", () => this.doUndo());
    this.redoBtn = this.makeAction(icons.redo, "Redo", () => this.doRedo());
    const eraseBtn = this.makeAction(icons.erase, "Erase", () => this.doErase());
    this.notesBtn = this.makeAction(icons.notes, "Notes", () => this.toggleNotes());
    const autoBtn = this.makeAction(icons.auto, "Auto", () => this.doAutoNotes());
    const hintBtn = this.makeAction(icons.hint, "Hint", () => this.doHint());
    const clearBtn = this.makeAction(icons.clear, "Clear", () => this.doClearBoard());
    actions.append(
      this.undoBtn,
      this.redoBtn,
      eraseBtn,
      this.notesBtn,
      autoBtn,
      hintBtn,
      clearBtn,
    );
    this.actionEls = {
      undo: this.undoBtn,
      redo: this.redoBtn,
      erase: eraseBtn,
      notes: this.notesBtn,
      auto: autoBtn,
      hint: hintBtn,
      clear: clearBtn,
    };

    this.notekindRow = el("div", "notekind-row");
    const kinds: { kind: NoteKind; label: string; cls: string }[] = [
      { kind: "pencil", label: "Pencil", cls: "pencil" },
      { kind: "accent", label: "Accent", cls: "accent" },
      { kind: "crossout", label: "Cross-out", cls: "crossout" },
    ];
    for (const k of kinds) {
      const b = el("button", `notekind ${k.cls}`);
      b.innerHTML = `<span class="dot"></span>${k.label}`;
      b.dataset.kind = k.kind;
      on(b, "click", () => this.setNoteKind(k.kind));
      this.notekindRow.appendChild(b);
    }

    const numpad = el("div", "numpad");
    for (let d = 1; d <= 9; d++) {
      const b = el("button", "num");
      b.innerHTML = `${d}<span class="count"></span>`;
      on(b, "click", () => this.onNumTap(d));
      numpad.appendChild(b);
      this.numEls.push(b);
    }

    controls.append(actions, this.notekindRow, numpad);
    shell.append(topbar, boardWrap, controls);

    // Developer-only color/theme editor, below the game. Vite replaces
    // import.meta.env.DEV with `false` in the production bundle, so this is
    // dropped for users but stays available to devs under `npm run dev`.
    if (import.meta.env.DEV) {
      const devHost = el("div", "dev-host");
      shell.append(devHost);
      this.devEditor = new DevColorEditor(
        devHost,
        THEMES,
        (id) => {
          this.settings.theme = id;
          save("settings", this.settings);
          this.applyTheme();
          this.render();
        },
        () => {
          // Themes were renamed / added / removed: rebuild the Settings picker
          // and re-validate the active theme.
          this.buildSettingsSheet();
          this.applyTheme();
          this.render();
        },
      );
    }

    // Overlays
    this.scrim = el("div", "scrim");
    on(this.scrim, "click", () => this.closeSheets());
    this.hintPanel = el("div", "hint-panel");
    this.settingsSheet = el("div", "sheet");
    this.diffSheet = el("div", "sheet");
    this.learnSheet = el("div", "sheet");
    this.kbdOverlay = el("div", "kbd-overlay");
    on(this.kbdOverlay, "click", () => this.closeKbdOverlay());
    this.toastEl = el("div", "toast");
    this.learn = new LearnView(this.learnSheet, () => this.closeSheets());
    this.buildSettingsSheet();
    this.buildDiffSheet();

    this.root.append(
      shell,
      this.scrim,
      this.hintPanel,
      this.settingsSheet,
      this.diffSheet,
      this.learnSheet,
      this.kbdOverlay,
      this.toastEl,
    );
  }

  private makeAction(icon: string, label: string, fn: () => void): HTMLButtonElement {
    const b = el("button", "action");
    b.innerHTML = `${icon}<span>${label}</span>`;
    on(b, "click", fn);
    return b;
  }

  private buildDiffSheet(): void {
    this.diffSheet.innerHTML = "";
    const head = el("div", "sheet-head");
    head.innerHTML = `<h2 style="margin:0">New Puzzle</h2>`;
    const close = el("button", "icon-btn");
    close.innerHTML = icons.close;
    on(close, "click", () => this.closeSheets());
    head.appendChild(close);
    this.diffSheet.appendChild(head);
    this.diffSheet.appendChild(
      el("p", "sub", "Each level is defined by the techniques it needs to solve."),
    );

    // Difficulty tiles in a two-column grid; Daily spans the full width.
    // Difficulty is shown by the accent color + the techniques blurb (no stars).
    const grid = el("div", "diff-grid");
    for (const d of DIFFICULTIES) {
      const tile = el("button", "diff-tile");
      tile.style.setProperty("--tile", DIFF_COLOR[d.id]);
      tile.innerHTML =
        `<span class="dn">${d.label}</span>` + `<span class="ds">${d.blurb}</span>`;
      on(tile, "click", () => void this.newGame(d.id));
      grid.appendChild(tile);
    }
    const daily = el("button", "diff-tile daily");
    daily.style.setProperty("--tile", "var(--primary)");
    daily.innerHTML =
      `<span class="dn">Daily Challenge</span>` +
      `<span class="ds">A new shared puzzle every day</span>`;
    on(daily, "click", () => void this.newGame("hard", true));
    grid.appendChild(daily);

    this.diffSheet.appendChild(grid);
  }

  private doClearBoard(): void {
    if (!this.game) return;
    sound.play("erase");
    this.game.clearBoard();
    this.selected = null;
    this.armed = null;
    this.lastDigit = null;
    this.multi.clear();
    this.hintCells.clear();
    this.closeSheets();
    this.persist();
    this.render();
    this.toast("Board cleared, Undo to restore");
  }

  // ---- Keyboard-shortcuts overlay ----------------------------------------
  // Blurs & dims the screen, then places key-hint badges directly over the
  // on-screen buttons whose function each key performs.

  private toggleKbdOverlay(): void {
    if (this.kbdOverlay.classList.contains("show")) this.closeKbdOverlay();
    else this.openKbdOverlay();
  }

  private closeKbdOverlay(): void {
    this.kbdOverlay.classList.remove("show");
    this.appShell?.classList.remove("kbd-dim");
  }

  private openKbdOverlay(): void {
    this.closeSheets();
    if (!this.game) return;
    const o = this.kbdOverlay;
    o.innerHTML = "";

    // A key-cap badge that sits just above the button it controls.
    const badge = (target: HTMLElement | null | undefined, label: string) => {
      if (!target) return;
      const r = target.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      const b = el("div", "kbd-badge");
      b.textContent = label;
      b.style.left = `${r.left + r.width / 2}px`;
      b.style.top = `${r.top}px`;
      o.appendChild(b);
    };

    // The control buttons each get their key in a grid that mirrors the action
    // row, so every key sits over its own button without crowding its neighbours.
    const actionsRow = this.actionEls.undo.parentElement;
    if (actionsRow) {
      const ar = actionsRow.getBoundingClientRect();
      const keyrow = el("div", "kbd-keyrow");
      keyrow.style.left = `${ar.left}px`;
      keyrow.style.top = `${ar.top}px`;
      keyrow.style.width = `${ar.width}px`;
      const keys = ["Ctrl+Z", "Ctrl+Y", "Del", "N", "", "H", ""]; // Auto & Clear have no key
      for (const k of keys) {
        const cell = el("div", "kc-cell");
        if (k) {
          const chip = el("div", "kc");
          chip.textContent = k;
          cell.appendChild(chip);
        }
        keyrow.appendChild(cell);
      }
      o.appendChild(keyrow);
    }

    // Note-type buttons (only visible in Notes mode), three wide buttons, so
    // simple badges don't crowd.
    if (this.notekindRow.classList.contains("show")) {
      const nk = (kind: string) =>
        this.notekindRow.querySelector(`[data-kind="${kind}"]`) as HTMLElement | null;
      badge(nk("pencil"), "P");
      badge(nk("accent"), "A");
      badge(nk("crossout"), "X");
    }

    // One badge above the number pad, the digits on the keys are self-evident.
    const first = this.numEls[0]?.getBoundingClientRect();
    const last = this.numEls[8]?.getBoundingClientRect();
    if (first && last && first.width) {
      const b = el("div", "kbd-badge");
      b.textContent = "1 – 9";
      b.style.left = `${(first.left + last.right) / 2}px`;
      b.style.top = `${first.top}px`;
      o.appendChild(b);
    }

    // A readable card (centred on the board) for things not tied to one button.
    const card = el("div", "kbd-card");
    card.innerHTML =
      `<div class="kc-title">On the board</div>` +
      `<div class="kr"><span class="cap">Arrows</span><span class="lbl">Move the selection</span></div>` +
      `<div class="kr"><span class="cap">Drag</span><span class="lbl">Select several cells</span></div>` +
      `<div class="kr"><span class="cap">Ctrl + click</span><span class="lbl">Add / remove a cell</span></div>` +
      `<div class="kr"><span class="cap">Esc</span><span class="lbl">Clear the selection</span></div>`;
    const br = this.boardEl.getBoundingClientRect();
    card.style.left = `${br.left + br.width / 2}px`;
    card.style.top = `${br.top + br.height / 2}px`;
    o.appendChild(card);

    const title = el("div", "kbd-title");
    title.textContent = "Keyboard shortcuts";
    o.appendChild(title);

    const foot = el("div", "kbd-foot");
    foot.innerHTML = "Press <b>K</b> or tap anywhere to close";
    o.appendChild(foot);

    this.appShell?.classList.add("kbd-dim");
    o.classList.add("show");
  }

  private buildSettingsSheet(): void {
    this.settingsSheet.innerHTML = "";
    const head = el("div", "sheet-head");
    head.innerHTML = `<h2 style="margin:0">Settings</h2>`;
    const close = el("button", "icon-btn");
    close.innerHTML = icons.close;
    on(close, "click", () => this.closeSheets());
    head.appendChild(close);
    this.settingsSheet.appendChild(head);

    // Theme picker, driven by the effective theme list (built-ins plus any
    // custom themes from the editor, with renames/deletions applied).
    const themeField = el("div", "field");
    themeField.innerHTML = `<span class="flabel">Theme</span>`;
    const grid = el("div", "theme-grid");
    for (const t of effectiveThemes(THEMES)) {
      const b = el("button", "theme-swatch");
      b.classList.toggle("on", this.settings.theme === t.id);
      const sc = this.themeColors(t.id);
      // Render the whole swatch in its OWN theme's colors so nothing in the
      // preview shifts when a different theme becomes active.
      b.style.background = sc.surface;
      b.style.color = sc.fg;
      b.dataset.accent = sc.accent;
      if (this.settings.theme === t.id) b.style.boxShadow = `inset 0 0 0 2px ${sc.accent}`;
      b.innerHTML =
        `<span class="sw" style="background:${sc.bg}">` +
        `<span class="num" style="color:${sc.fg}">7</span>` +
        `<span class="a" style="background:${sc.accent}"></span></span>` +
        `<span class="name">${t.name}</span>` +
        `<span class="check" style="color:${sc.accent}">✓</span>`;
      on(b, "click", () => {
        this.settings.theme = t.id;
        save("settings", this.settings);
        this.applyTheme();
        for (const c of Array.from(grid.children) as HTMLElement[]) {
          c.classList.toggle("on", c === b);
          c.style.boxShadow = c === b ? `inset 0 0 0 2px ${c.dataset.accent}` : "";
        }
        this.devEditor?.refresh();
      });
      grid.appendChild(b);
    }
    themeField.appendChild(grid);
    this.settingsSheet.appendChild(themeField);

    type BoolKey =
      | "assist"
      | "autoCandidates"
      | "autoSolve"
      | "sound"
      | "highlightPeers"
      | "highlightSame"
      | "showTimer"
      | "showCounts";
    const mk = (title: string, desc: string, key: BoolKey) => {
      const row = el("div", "toggle-row");
      row.innerHTML = `<div class="desc"><span class="t">${title}</span><span class="d">${desc}</span></div>`;
      const sw = el("div", `switch ${this.settings[key] ? "on" : ""}`);
      on(sw, "click", () => {
        this.settings[key] = !this.settings[key];
        sw.classList.toggle("on", this.settings[key]);
        save("settings", this.settings);
        if (key === "sound") sound.enabled = this.settings.sound;
        if (this.game) {
          if (key === "assist") this.game.assist = this.settings.assist;
          if (key === "autoCandidates") this.game.setAutoCandidates(this.settings.autoCandidates);
          if (key === "autoSolve") this.game.autoSolve = this.settings.autoSolve;
        }
        this.render();
      });
      row.appendChild(sw);
      this.settingsSheet.appendChild(row);
    };
    mk("Auto-solve forced cells", "After a correct entry, fill cells that become forced, a satisfying chain reaction.", "autoSolve");
    mk("Sound effects", "Play sounds for taps, placements and the auto-solve chain.", "sound");
    mk("Highlight mistakes", "Flag entries that disagree with the solution.", "assist");
    mk("Auto-fill notes", "Keep legal pencil marks filled in as you play.", "autoCandidates");
    mk("Highlight peers", "Tint the selected cell's row, column and box.", "highlightPeers");
    mk("Highlight same number", "Light up every copy of the active digit.", "highlightSame");
    mk("Show timer", "Display the elapsed time (tap it to pause either way).", "showTimer");
    mk("Show remaining counts", "Badge each number with how many are left.", "showCounts");
  }

  // -------------------------------------------------------------- pointer / drag

  private cellFromPoint(x: number, y: number): number | null {
    const target = document.elementFromPoint(x, y) as HTMLElement | null;
    const cell = target?.closest(".cell") as HTMLElement | null;
    if (!cell || cell.dataset.index === undefined) return null;
    return Number(cell.dataset.index);
  }

  private bindBoardPointer(board: HTMLElement): void {
    on(board, "pointerdown", (e) => {
      if (this.paused || !this.game) return;
      const i = this.cellFromPoint(e.clientX, e.clientY);
      if (i === null) return;
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      this.pointerDown = true;
      this.ctrlDown = e.ctrlKey || e.metaKey;
      this.dragStart = i;
      this.dragMoved = false;
      this.dragSet = new Set([i]);
      this.hintCells.clear();
    });
    on(board, "pointermove", (e) => {
      if (!this.pointerDown) return;
      const i = this.cellFromPoint(e.clientX, e.clientY);
      if (i === null || this.dragSet.has(i)) return;
      this.dragSet.add(i);
      if (i !== this.dragStart) this.dragMoved = true;
      this.renderDragPreview();
    });
    const end = () => {
      if (!this.pointerDown) return;
      this.pointerDown = false;
      if (this.dragMoved && this.dragSet.size > 1) {
        // Drag selects multiple cells. With Ctrl held, extend the existing
        // selection instead of replacing it.
        if (this.ctrlDown) for (const c of this.dragSet) this.multi.add(c);
        else this.multi = new Set(this.dragSet);
        this.selected = null;
        this.armed = null;
      } else if (this.dragStart !== null) {
        if (this.ctrlDown) {
          // Ctrl + click toggles a single cell in the multi-selection.
          if (this.multi.has(this.dragStart)) this.multi.delete(this.dragStart);
          else this.multi.add(this.dragStart);
          this.selected = null;
          this.armed = null;
        } else {
          this.selectCell(this.dragStart);
        }
      }
      this.dragSet.clear();
      this.dragStart = null;
      this.render();
    };
    on(board, "pointerup", end);
    on(board, "pointercancel", end);
  }

  private renderDragPreview(): void {
    for (let i = 0; i < 81; i++)
      this.cellEls[i].classList.toggle("dragsel", this.dragSet.has(i));
  }

  // -------------------------------------------------------------- input

  private selectCell(i: number): void {
    if (!this.game) return;
    this.multi.clear();
    if (this.armed !== null) {
      this.applyDigitToCells([i], this.armed);
      this.selected = i;
    } else {
      this.selected = this.selected === i ? null : i;
      sound.play("tap");
    }
  }

  private onNumTap(d: number): void {
    if (!this.game || this.paused) return;
    this.hintCells.clear();
    if (this.multi.size > 0 && this.armed === null) {
      this.applyDigitToCells([...this.multi], d);
    } else if (this.selected !== null && this.armed === null) {
      this.applyDigitToCells([this.selected], d);
    } else {
      this.armed = this.armed === d ? null : d;
      this.selected = null;
      this.multi.clear();
      sound.play("toggle");
    }
    this.render();
  }

  private applyDigitToCells(cells: number[], d: number): void {
    if (!this.game) return;
    let mistake = false;
    let solved = false;
    let placed = false;
    let noted = false;
    const autoFilled: number[] = [];
    for (const i of cells) {
      if (this.game.isGiven(i)) continue;
      if (this.mode === "notes") {
        if (this.game.getValue(i) === 0) {
          this.game.toggleNote(i, d, this.noteKind);
          noted = true;
        }
      } else {
        const res = this.game.setValue(i, d);
        if (res.changed) placed = true;
        if (res.mistake) mistake = true;
        if (res.solved) solved = true;
        for (const c of res.autoFilled) autoFilled.push(c);
      }
    }
    if (noted) sound.play("note");
    else if (mistake) sound.play("mistake");
    else if (placed) sound.play("place");

    if (mistake) this.toast("That doesn't fit here");
    this.persist();
    this.render();
    if (autoFilled.length) this.animateCascade(autoFilled);
    if (solved) {
      // Let the chain reaction finish before celebrating.
      if (autoFilled.length) window.setTimeout(() => this.onSolved(), autoFilled.length * 55 + 200);
      else this.onSolved();
    }
  }

  /** Ripple flash + ascending sound across the cells the cascade resolved. */
  private animateCascade(cells: number[]): void {
    const step = 55;
    cells.forEach((c, k) => {
      window.setTimeout(() => {
        const cell = this.cellEls[c];
        cell.classList.remove("justfilled");
        void cell.offsetWidth; // restart the animation
        cell.classList.add("justfilled");
        sound.cascade(k);
        window.setTimeout(() => cell.classList.remove("justfilled"), 520);
      }, k * step);
    });
  }

  private doErase(): void {
    if (!this.game) return;
    let changed = false;
    if (this.multi.size > 0) {
      for (const i of this.multi) if (this.game.clear(i).changed) changed = true;
    } else if (this.selected !== null) {
      if (this.game.clear(this.selected).changed) changed = true;
    } else if (this.armed !== null) {
      this.armed = null;
    }
    if (changed) sound.play("erase");
    this.persist();
    this.render();
  }

  private doUndo(): void {
    if (this.game?.undo()) {
      sound.play("undo");
      this.persist();
      this.render();
    }
  }
  private doRedo(): void {
    if (this.game?.redo()) {
      sound.play("redo");
      this.persist();
      this.render();
    }
  }

  private toggleNotes(): void {
    this.mode = this.mode === "notes" ? "normal" : "notes";
    sound.play("toggle");
    this.render();
  }

  private setNoteKind(kind: NoteKind): void {
    this.noteKind = kind;
    if (this.mode !== "notes") this.mode = "notes";
    sound.play("toggle");
    this.render();
  }

  private doAutoNotes(): void {
    if (!this.game) return;
    this.game.autoFillNotes();
    sound.play("button");
    this.toast("Pencil marks filled");
    this.persist();
    this.render();
  }

  private doHint(): void {
    if (!this.game || this.paused) return;
    const hint = this.game.hint();
    if (!hint) {
      this.toast("No logical move found from here");
      return;
    }
    sound.play("hint");
    this.hintCells = new Set(hint.highlightCells);
    this.showHint(hint);
    this.render();
  }

  // -------------------------------------------------------------- hint panel

  private showHint(hint: Hint): void {
    this.hintPanel.innerHTML = "";
    const tier = el("span", "tier", `${DIFFICULTY_BY_ID[hint.difficulty].label} · Tier ${hint.tier}`);
    const h3 = el("h3", undefined, hint.title);
    const learnable = LearnView.has(hint.technique);
    const lead = el(
      "p",
      "summary",
      learnable
        ? `A ${hint.title} works here. Get the move, or open the lesson to learn the idea.`
        : `A ${hint.title} works here.`,
    );
    const body = el("div", "hint-body");
    this.hintPanel.append(tier, h3, lead, body);

    // Stage 1: choose between the move and the tutorial.
    const renderChoice = () => {
      body.innerHTML = "";
      const actions = el("div", "hint-actions");
      if (learnable) {
        const lesson = el("button", "btn");
        lesson.textContent = "Go to lesson";
        on(lesson, "click", () => this.openLearn(hint.technique));
        actions.appendChild(lesson);
      }
      const show = el("button", "btn primary");
      show.textContent = "Show hint";
      on(show, "click", renderHint);
      actions.appendChild(show);
      body.appendChild(actions);
    };

    // Stage 2: the full hint, still linking back to the lesson.
    const renderHint = () => {
      body.innerHTML = "";
      const summary = el("p", "summary", hint.summary);
      const expl = el("p", "explanation", hint.explanation);
      body.append(summary, expl);
      if (learnable) {
        const link = el("button", "hint-link", `Learn the ${hint.title} technique →`);
        on(link, "click", () => this.openLearn(hint.technique));
        body.appendChild(link);
      }
      const actions = el("div", "hint-actions");
      const got = el("button", "btn");
      got.textContent = "Got it";
      on(got, "click", () => this.closeSheets());
      const apply = el("button", "btn primary");
      apply.textContent = hint.step.placements.length ? "Place it" : "Apply";
      on(apply, "click", () => {
        this.applyHint(hint);
        this.closeSheets();
      });
      actions.append(got, apply);
      body.appendChild(actions);
    };

    renderChoice();
    this.scrim.classList.add("show");
    this.hintPanel.classList.add("show");
  }

  private applyHint(hint: Hint): void {
    if (!this.game) return;
    const autoFilled: number[] = [];
    let solved = false;
    if (hint.step.placements.length) {
      sound.play("place");
      for (const p of hint.step.placements) {
        const res = this.game.setValue(p.cell, p.digit);
        if (res.solved) solved = true;
        for (const c of res.autoFilled) autoFilled.push(c);
      }
    } else {
      for (const e of hint.step.eliminations) {
        if (this.game.getValue(e.cell) === 0) this.game.toggleNote(e.cell, e.digit, "crossout");
      }
    }
    this.hintCells.clear();
    if (autoFilled.length) this.animateCascade(autoFilled);
    if (solved) {
      if (autoFilled.length) window.setTimeout(() => this.onSolved(), autoFilled.length * 55 + 200);
      else this.onSolved();
    }
    this.persist();
    this.render();
  }

  // -------------------------------------------------------------- rendering

  private render(): void {
    if (!this.game) return;
    const g = this.game;

    const def = DIFFICULTY_BY_ID[this.difficulty];
    this.diffChipEl.innerHTML = `<span class="dot" style="background:${DIFF_COLOR[this.difficulty]}"></span>${this.isDaily ? "Daily · " : ""}${def.label}`;
    this.mistakeEl.textContent = String(g.mistakeCount);
    this.timeEl.textContent = this.paused
      ? "Paused"
      : this.settings.showTimer
        ? fmtTime(this.elapsed)
        : "•••";

    const activeValue =
      this.armed !== null ? this.armed : this.selected !== null ? g.getValue(this.selected) : 0;
    // Remember the active value so its highlight persists over later empty-cell
    // taps, until the player clicks off the board or highlights another value.
    if (activeValue > 0) this.lastDigit = activeValue;
    const highlightValue = activeValue > 0 ? activeValue : (this.lastDigit ?? 0);
    const peerSet =
      this.settings.highlightPeers && this.selected !== null ? new Set(g.peersOf(this.selected)) : null;
    const sameDigit =
      this.settings.highlightSame && highlightValue > 0 ? new Set(g.cellsWithValue(highlightValue)) : null;
    const conflicts = g.conflicts();

    for (let i = 0; i < 81; i++) {
      const cv = g.getCell(i);
      const cls = [STATIC_CLASS[i]];
      if (cv.given) cls.push("given");
      else if (cv.value) cls.push("entered");
      if (this.multi.has(i)) cls.push("selected");
      else if (this.selected === i) cls.push("selected");
      else if (peerSet && peerSet.has(i)) cls.push("peer");
      if (sameDigit && sameDigit.has(i)) cls.push("samedigit");
      if (conflicts.has(i)) cls.push("conflict");
      if (cv.mistake) cls.push("mistake");
      if (this.hintCells.has(i)) cls.push("hint");
      // Preserve the transient cascade-ripple class through re-renders.
      if (this.cellEls[i].classList.contains("justfilled")) cls.push("justfilled");
      this.cellEls[i].className = cls.join(" ");

      const valueEl = this.valueEls[i];
      const notesEl = valueEl.nextElementSibling as HTMLElement;
      if (cv.value) {
        if (this.lastValues[i] !== cv.value) {
          valueEl.textContent = String(cv.value);
          valueEl.classList.remove("value");
          void valueEl.offsetWidth;
          valueEl.classList.add("value");
        }
        valueEl.style.display = "";
        notesEl.style.display = "none";
      } else {
        valueEl.style.display = "none";
        notesEl.style.display = "";
        const spans = this.noteSpanEls[i];
        for (let d = 1; d <= 9; d++) {
          const s = spans[d];
          let kind = "";
          if (hasDigit(cv.accent, d)) kind = "accent";
          else if (hasDigit(cv.crossout, d)) kind = "crossout";
          else if (hasDigit(cv.pencil, d)) kind = "pencil";
          if (kind) {
            s.textContent = String(d);
            const hl = kind === "pencil" && activeValue === d ? " hl" : "";
            s.className = (kind === "pencil" ? "" : kind) + hl;
          } else {
            s.textContent = "";
            s.className = "";
          }
        }
      }
      this.lastValues[i] = cv.value;
    }

    for (let d = 1; d <= 9; d++) {
      const b = this.numEls[d - 1];
      const remaining = g.remainingCount(d);
      b.classList.toggle("armed", this.armed === d);
      b.classList.toggle("done", remaining <= 0);
      b.classList.toggle("hide-count", !this.settings.showCounts);
      const count = b.querySelector(".count");
      if (count) count.textContent = remaining > 0 ? String(remaining) : "";
    }

    this.notesBtn.classList.toggle("on", this.mode === "notes");
    this.notekindRow.classList.toggle("show", this.mode === "notes");
    for (const b of Array.from(this.notekindRow.children) as HTMLElement[]) {
      b.classList.toggle("on", b.dataset.kind === this.noteKind);
    }
    this.undoBtn.toggleAttribute("disabled", !g.canUndo());
    this.redoBtn.toggleAttribute("disabled", !g.canRedo());
  }

  // -------------------------------------------------------------- pause / timer

  private togglePause(): void {
    sound.play("toggle");
    if (this.paused) this.resume();
    else this.pause();
  }
  private pause(): void {
    if (!this.game || this.paused || this.game.isSolved()) return;
    this.paused = true;
    this.boardEl.classList.add("paused");
    this.pauseOverlay.classList.add("show");
    this.render();
  }
  private resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.boardEl.classList.remove("paused");
    this.pauseOverlay.classList.remove("show");
    this.render();
  }

  private startTimer(): void {
    this.stopTimer();
    this.timerId = window.setInterval(() => {
      if (this.game && !this.paused && !this.game.isSolved()) {
        this.elapsed++;
        if (!this.paused && this.settings.showTimer) this.timeEl.textContent = fmtTime(this.elapsed);
        if (this.elapsed % 5 === 0) this.persist();
      }
    }, 1000);
  }
  private stopTimer(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  // -------------------------------------------------------------- misc

  private onSolved(): void {
    this.stopTimer();
    sound.play("win");
    this.toast(`Solved in ${fmtTime(this.elapsed)}!`);
    this.confetti();
    save("current", null);
    setTimeout(() => this.openDiffSheet(), 1800);
  }

  private persist(): void {
    if (!this.game) return;
    save("current", {
      difficulty: this.difficulty,
      game: this.game.serialize(),
      elapsed: this.elapsed,
      isDaily: this.isDaily,
    });
  }

  private openDiffSheet(): void {
    this.closeSheets();
    this.scrim.classList.add("show");
    this.diffSheet.classList.add("show");
  }
  private openSettings(): void {
    this.closeSheets();
    this.scrim.classList.add("show");
    this.settingsSheet.classList.add("show");
  }
  private openLearn(techniqueId?: string): void {
    this.closeSheets();
    this.learn.open(techniqueId);
    this.scrim.classList.add("show");
    this.learnSheet.classList.add("show");
  }
  private closeSheets(): void {
    this.scrim.classList.remove("show");
    this.hintPanel.classList.remove("show");
    this.settingsSheet.classList.remove("show");
    this.diffSheet.classList.remove("show");
    this.learnSheet.classList.remove("show");
    this.closeKbdOverlay();
  }

  private toastTimeout: number | null = null;
  private toast(msg: string): void {
    this.toastEl.textContent = msg;
    this.toastEl.classList.add("show");
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    this.toastTimeout = window.setTimeout(() => this.toastEl.classList.remove("show"), 2200);
  }

  private confetti(): void {
    const colors = ["#2566f0", "#ff5c7a", "#ffc857", "#1bb98a", "#8b5cf6"];
    for (let i = 0; i < 60; i++) {
      const c = el("div", "confetti");
      c.style.left = Math.random() * 100 + "vw";
      c.style.background = colors[i % colors.length];
      const dur = 1.6 + Math.random() * 1.4;
      c.animate(
        [
          { transform: `translateY(0) rotate(0deg)`, opacity: 1 },
          { transform: `translateY(100vh) rotate(${Math.random() * 720}deg)`, opacity: 0.9 },
        ],
        { duration: dur * 1000, easing: "cubic-bezier(.2,.6,.4,1)" },
      );
      document.body.appendChild(c);
      setTimeout(() => c.remove(), dur * 1000);
    }
  }

  // -------------------------------------------------------------- keyboard

  private bindKeyboard(): void {
    on(document, "keydown", (e) => {
      if (!this.game) return;
      // When the user is typing in a field (e.g. the theme editor), let the
      // field handle every key, don't fire game shortcuts or undo/redo.
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      const k = e.key;
      const ctrl = e.ctrlKey || e.metaKey;

      // Undo / redo: Ctrl+Z and Ctrl+Y (Shift+Z also redoes).
      if (ctrl && (k === "z" || k === "Z")) {
        e.preventDefault();
        if (e.shiftKey) this.doRedo();
        else this.doUndo();
        return;
      }
      if (ctrl && (k === "y" || k === "Y")) {
        e.preventDefault();
        this.doRedo();
        return;
      }
      if (ctrl) return; // leave other ctrl combos to the browser

      if (k >= "1" && k <= "9") {
        this.onNumTap(Number(k));
      } else if (k === "Backspace" || k === "Delete" || k === "0") {
        this.doErase();
      } else if (k === "n" || k === "N" || k === " ") {
        e.preventDefault();
        this.toggleNotes();
      } else if (k === "p" || k === "P") {
        this.setNoteKind("pencil");
      } else if (k === "a" || k === "A") {
        this.setNoteKind("accent");
      } else if (k === "x" || k === "X") {
        this.setNoteKind("crossout");
      } else if (k === "h" || k === "H") {
        this.doHint();
      } else if (k === "k" || k === "K") {
        this.toggleKbdOverlay();
      } else if (k.startsWith("Arrow")) {
        e.preventDefault();
        const base = this.selected ?? 40;
        let nr = rowOf(base);
        let nc = colOf(base);
        if (k === "ArrowUp") nr = (nr + 8) % 9;
        if (k === "ArrowDown") nr = (nr + 1) % 9;
        if (k === "ArrowLeft") nc = (nc + 8) % 9;
        if (k === "ArrowRight") nc = (nc + 1) % 9;
        this.selected = nr * 9 + nc;
        this.multi.clear();
        this.armed = null;
        this.render();
      } else if (k === "Escape") {
        this.selected = null;
        this.armed = null;
        this.multi.clear();
        this.hintCells.clear();
        this.closeSheets();
        this.render();
      }
    });
  }
}
